import { type MutableRefObject, useContext, useEffect, useRef, useState } from "react"

import { AssetCollection, Index } from "wealthmode-frontend/backend"
import {
  Box,
  Button,
  ButtonText,
  FormControl,
  FormControlLabel,
  FormControlLabelText,
  Input,
  InputField,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  VStack,
} from "wealthmode-frontend/gluestack/ui"

import { BackendContext } from "@/context"

async function resolveAssetsCollection(data: Index): Promise<AssetCollection | undefined> {
  await data.fetch({ reload: true })
  return data.link("assets")?.resolve() as AssetCollection | undefined
}

function AddAssetForm({
  onClose,
  onSuccess,
  assetsCollectionRef,
}: {
  onClose: () => void
  onSuccess: () => void
  assetsCollectionRef: MutableRefObject<AssetCollection | null>
}) {
  const backend = useContext(BackendContext)

  const [market, setMarket] = useState("US")
  const [symbol, setSymbol] = useState("")
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function close() {
    if (submitting) {
      return
    }

    onClose()
  }

  async function submit() {
    setSubmitError(null)
    setSubmitting(true)

    try {
      let assets = assetsCollectionRef.current
      if (!assets) {
        const data = backend.link("data").resolve() as Index
        assets = (await resolveAssetsCollection(data)) ?? null
        assetsCollectionRef.current = assets
      }

      const listLink = assets?.link("self")
      if (!listLink) {
        setSubmitError("Assets resource is not available")
        return
      }

      const response = await listLink.send({
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          market: market.trim(),
          symbol: symbol.trim(),
        }),
      })

      if (!response.ok) {
        const text = await response.text()
        setSubmitError(text || response.statusText)
        return
      }

      onClose()
      onSuccess()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <ModalBody>
        <VStack className="gap-4">
          <FormControl>
            <FormControlLabel>
              <FormControlLabelText>Market</FormControlLabelText>
            </FormControlLabel>
            <Input
              variant="outline"
              size="md"
            >
              <InputField
                value={market}
                onChangeText={setMarket}
                autoCapitalize="none"
                autoCorrect={false}
              />
            </Input>
          </FormControl>
          <FormControl>
            <FormControlLabel>
              <FormControlLabelText>Symbol</FormControlLabelText>
            </FormControlLabel>
            <Input
              variant="outline"
              size="md"
            >
              <InputField
                value={symbol}
                onChangeText={setSymbol}
                autoCapitalize="none"
                autoCorrect={false}
              />
            </Input>
          </FormControl>
          {submitError ? (
            <Box className="rounded-md border border-border-medium bg-background-tertiary px-3 py-2">
              <Text className="text-sm text-foreground-secondary whitespace-pre-wrap">{submitError}</Text>
            </Box>
          ) : null}
        </VStack>
      </ModalBody>
      <ModalFooter className="gap-2">
        <Button
          variant="outline"
          onPress={close}
          isDisabled={submitting}
        >
          <ButtonText>Cancel</ButtonText>
        </Button>
        <Button
          action="primary"
          onPress={() => void submit()}
          isDisabled={submitting}
          className="bg-background-contrast"
        >
          <ButtonText className="text-foreground-contrast">{submitting ? "Adding…" : "Add"}</ButtonText>
        </Button>
      </ModalFooter>
    </>
  )
}

export function AddAssetModal({
  isOpen,
  onClose,
  onSuccess,
  assetsCollectionRef,
}: {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  assetsCollectionRef: MutableRefObject<AssetCollection | null>
}) {
  const [formKey, setFormKey] = useState(0)
  const wasOpen = useRef(false)

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setFormKey((key) => key + 1)
    }

    wasOpen.current = isOpen
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalBackdrop />
      <ModalContent className="bg-background-secondary border border-border-medium max-w-md w-full">
        <ModalHeader>
          <Text className="text-lg font-semibold text-foreground-primary">Add asset</Text>
          <ModalCloseButton />
        </ModalHeader>
        {isOpen ? (
          <AddAssetForm
            key={formKey}
            onClose={onClose}
            onSuccess={onSuccess}
            assetsCollectionRef={assetsCollectionRef}
          />
        ) : null}
      </ModalContent>
    </Modal>
  )
}
